import { Box, VStack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { useLEAccordionItemContext } from '../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import Loader from '../../../../components/Loader';
import VehicleInfo from '../../../../components/VehicleInfo';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import {
  KbbVinResult,
  Maybe,
  useKbbVinQuery,
  useTemporaryInfoUpdateMutation,
} from '../../../../gql/generated/graphql';
import { useTempInfo } from '../../../../hooks/useTempInfo';
import { notRequiredString } from '../../../../utils/strings';
import SelectTrim from './components/SelectTrim';
import SomethingsNotRight from './components/SomethingsNotRight';

interface FormValues {
  vehicleTrim: Maybe<string>;
  lienholder?: string;
  other_lienholder?: string;
}

const LooksRightItem = () => {
  const [refresh, setRefresh] = useState(false);

  const { info, infoLoading } = useTempInfo({ refresh });
  const { updateState, index, setOpenAccordionIndex } = useLEAccordionItemContext();
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();

  const tempInfoData = info?.data;
  const initialTrim = tempInfoData?.kbb_trim_name;

  const { data: vinData, loading: kbbLoading } = useKbbVinQuery({
    variables: { vin: tempInfoData?.vin as string },
    skip: !tempInfoData?.vin,
  });

  const options = Array.from(
    new Set(
      vinData?.kbbVin?.vinResults?.map((vinResult) => ({
        label: vinResult?.trimName || '',
        value: vinResult?.trimName || '',
      })),
    ),
  );

  const handleSubmit = async (values: FormValues) => {
    const vehicle: Maybe<KbbVinResult> = vinData?.kbbVin?.vinResults?.find(
      (vinResult) => vinResult?.trimName === values.vehicleTrim,
    );

    updateState({ status: 'loading' });

    await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data: {
            kbb_vehicle_id: vehicle?.vehicleId,
            kbb_vehicle_name: vehicle?.vehicleName,
            kbb_trim_name: vehicle?.trimName,
            kbb_all_options: vehicle?.vehicleOptions,
          },
        },
      },
    });

    updateState({ status: 'complete' });
    setOpenAccordionIndex(index + 1);
  };

  if (infoLoading) {
    return <Loader />;
  }

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={{
        vehicleTrim: options.length === 1 ? options[0].label : initialTrim,
        lienholder: tempInfoData?.lienholder ?? '',
        other_lienholder: tempInfoData?.other_lienholder ?? '',
      }}
      validateOnChange
      validateOnBlur
      onSubmit={handleSubmit}
      validationSchema={Yup.object({
        vehicleTrim:
          options.length || initialTrim
            ? Yup.string().required('Please select a trim').nullable()
            : notRequiredString,
      })}
    >
      {({ isSubmitting }) => (
        <Form style={{ width: '100%' }}>
          <Box color="leaseEndBlue" mt={2}>
            <VStack>
              <VehicleInfo
                year={info?.data.year ?? ''}
                make={info?.data.make ?? ''}
                model={info?.data.model ?? ''}
                vin={info?.data.vin ?? ''}
                infoLoading={infoLoading}
              />

              <SomethingsNotRight info={info} setRefresh={setRefresh} />

              <SelectTrim kbbLoading={kbbLoading} options={options} />

              <PrimaryButton mt={4} type="submit" isLoading={isSubmitting}>
                NEXT
              </PrimaryButton>
            </VStack>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default LooksRightItem;
